import { createApp } from 'vue';
import { pinia } from '@/store/index.js';
import VueGtag from 'vue-gtag';
import VueConfetti from 'vue-confetti';
import vueAwesomeCountdown from 'vue-awesome-countdown';
import App from '@/App.vue';
import PublicLayout from '@/layouts/PublicLayout.vue';
import PrivateLayout from '@/layouts/PrivateLayout.vue';
import router from '@/router';
import consts from '@/consts';
import vuetify from '@/plugins/vuetify';
import { setupI18n } from '@/plugins/i18n';
import { useAuthStore } from '@/store/auth.module';
import rank from '@/services/rank';
import '@/assets/styles/main.scss';
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';
import { fab } from '@fortawesome/free-brands-svg-icons';
import * as Sentry from "@sentry/vue";
import { useUserStore } from '@/store/user.module';

const app = createApp(App);

app.component('font-awesome-icon', FontAwesomeIcon);
library.add(fas, far, fab);

app.component('PublicLayout', PublicLayout);
app.component('PrivateLayout', PrivateLayout);

app.config.globalProperties.$consts = consts;

app.use(pinia);
const authStore = useAuthStore();
const i18n = setupI18n(authStore);
i18n.global.locale = authStore.getLocale;

app.use(router);
app.use(vuetify);
app.use(i18n);
app.use(VueGtag, {
  bootstrap: false,
  config: {
    id: 'G-P83L4KEFE3',
    params: {
      anonymize_ip: true,
    },
  },
}, router);
app.use(VueConfetti);
app.use(vueAwesomeCountdown);

Sentry.init({
  app,
  ignoreErrors: ['Request failed with status code 401'],
  dsn: import.meta.env.VITE_SENTRY_DNS,
  release: import.meta.env.npm_package_version,
  integrations: [
    Sentry.browserTracingIntegration({ router }),
  ],
  tracesSampleRate: 1.0,
});

Sentry.init({
  enabled: import.meta.env.PROD,
  app,
  dsn: import.meta.env.VITE_SENTRY_DNS,
  integrations: [
    Sentry.replayIntegration(),
    Sentry.browserTracingIntegration({ router }),
  ],
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    for (const registration of registrations) {
      registration.unregister();
    }
  });
}

const user = useUserStore().currentUser;

if (user) {
  Sentry.setUser(user);
}

app.config.globalProperties.$filters = {
  rank,
};


app.mount('#app');
